import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'flowbite-react';
import { HiExclamation, HiOutlineArchive, HiOutlineTrash } from 'react-icons/hi';
import { schoolsApi } from '../../api/schools';
import { ApiError } from '../../api/client';

type Stage = 'choose' | 'confirmClose' | 'confirmPermanent' | 'submitting';

/** Map of blocker key -> count, returned by the backend on 409 Conflict. */
type Blockers = Record<string, number>;

/** Human-friendly label per blocker key. */
const BLOCKER_LABELS: Record<string, string> = {
  staffAssignments: 'Staff assignments',
  metrics: 'Organization metrics',
  municipalityLinks: 'Municipality links',
  calendars: 'Calendars',
  userAccounts: 'User accounts scoped to this school',
  personIdentificationCodes: 'Person identification codes issued by this school',
  childOrganizations: 'Child organizations',
};

interface DeleteSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful soft or hard delete. */
  onSuccess: (kind: 'closed' | 'permanentlyDeleted') => void;
  school: {
    schoolId: number;
    nameOfInstitution: string;
  };
}

export const DeleteSchoolModal: React.FC<DeleteSchoolModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  school,
}) => {
  const [stage, setStage] = useState<Stage>('choose');
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<Blockers | null>(null);

  // Reset state whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStage('choose');
      setError(null);
      setBlockers(null);
    }
  }, [isOpen]);

  const handleSoftDelete = async () => {
    setError(null);
    setBlockers(null);
    setStage('submitting');
    try {
      await schoolsApi.delete(school.schoolId);
      onSuccess('closed');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to close school';
      setError(message);
      setStage('confirmClose');
    }
  };

  const handleHardDelete = async () => {
    setError(null);
    setBlockers(null);
    setStage('submitting');
    try {
      await schoolsApi.deletePermanent(school.schoolId);
      onSuccess('permanentlyDeleted');
    } catch (err) {
      // 409 Conflict carries structured detail: { message, blockers, ... }
      if (err instanceof ApiError && err.status === 409) {
        const detail = err.response?.detail;
        if (detail && typeof detail === 'object') {
          setError(detail.message || 'Cannot permanently delete this school.');
          setBlockers(detail.blockers || null);
        } else {
          setError('Cannot permanently delete this school.');
        }
      } else {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to permanently delete school';
        setError(message);
      }
      setStage('confirmPermanent');
    }
  };

  const renderChooseStage = () => (
    <>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        How do you want to remove <span className="font-semibold">{school.nameOfInstitution}</span>?
      </p>

      {/* Close School option (default / recommended) */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 p-2 bg-blue-50 dark:bg-blue-900 rounded-full">
            <HiOutlineArchive className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900 dark:text-white">Close School</h4>
              <span className="text-xs uppercase tracking-wider bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                Recommended
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Marks the school as <em>Closed</em>. Records stay in the database for
              historical reference (board minutes, staff history, etc.).
            </p>
            <button
              type="button"
              onClick={() => setStage('confirmClose')}
              className="mt-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Close School
            </button>
          </div>
        </div>
      </div>

      {/* Permanent delete option */}
      <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50/30 dark:bg-red-900/10">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 p-2 bg-red-100 dark:bg-red-900 rounded-full">
            <HiOutlineTrash className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 dark:text-white">Permanently Delete</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Hard delete. Removes the school row and its owned children (address,
              phone, email) from the database. Refuses if any staff or metrics
              reference this school — clean those up first.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 italic">
              Use this for correcting bad data (e.g. duplicates), not for closing a real school.
            </p>
            <button
              type="button"
              onClick={() => setStage('confirmPermanent')}
              className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
            >
              Permanently Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderConfirmClose = () => (
    <>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        <span className="font-semibold">{school.nameOfInstitution}</span> will be
        marked as <em>Closed</em> and hidden from the schools list by default.
        Records remain in the database. To bring it back later, enable
        <em> "Show closed schools" </em> in the Schools tab header, then click
        the green refresh icon on the school's row to reopen it.
      </p>

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button color="gray" onClick={() => setStage('choose')}>
          Back
        </Button>
        <button
          type="button"
          onClick={handleSoftDelete}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          Confirm: Close School
        </button>
      </div>
    </>
  );

  const renderConfirmPermanent = () => (
    <>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        <span className="font-semibold">{school.nameOfInstitution}</span> will be
        removed from the database along with its address, phone, and email
        records. <strong>This cannot be undone.</strong>
      </p>

      {error && (
        <Alert color="failure" className="mb-3">
          <div className="font-medium">{error}</div>
          {blockers && Object.keys(blockers).length > 0 && (
            <ul className="mt-2 list-disc list-inside text-sm">
              {Object.entries(blockers).map(([key, count]) => (
                <li key={key}>
                  <span className="font-medium">{BLOCKER_LABELS[key] || key}:</span>{' '}
                  {count}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button color="gray" onClick={() => setStage('choose')}>
          Back
        </Button>
        <button
          type="button"
          onClick={handleHardDelete}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
        >
          Confirm: Permanently Delete
        </button>
      </div>
    </>
  );

  const renderSubmitting = () => (
    <div className="flex items-center justify-center py-8">
      <div className="text-sm text-gray-600 dark:text-gray-400">Working…</div>
    </div>
  );

  // Header content varies by stage so the user always sees the title/icon
  // matching the action they're about to confirm.
  const header = (() => {
    switch (stage) {
      case 'confirmClose':
        return {
          title: 'Close School',
          Icon: HiOutlineArchive,
          iconBg: 'bg-blue-100 dark:bg-blue-900',
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      case 'confirmPermanent':
        return {
          title: 'Permanently Delete School',
          Icon: HiExclamation,
          iconBg: 'bg-red-100 dark:bg-red-900',
          iconColor: 'text-red-600 dark:text-red-400',
        };
      // 'choose' and 'submitting' both show the neutral parent title
      default:
        return {
          title: 'Delete School',
          Icon: HiOutlineTrash,
          iconBg: 'bg-red-100 dark:bg-red-900',
          iconColor: 'text-red-600 dark:text-red-400',
        };
    }
  })();
  const HeaderIcon = header.Icon;

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg">
      <div className="flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${header.iconBg} rounded-lg flex items-center justify-center`}>
              <HeaderIcon className={`w-6 h-6 ${header.iconColor}`} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {header.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {school.nameOfInstitution}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 flex-1 overflow-y-auto">
          {stage === 'choose' && renderChooseStage()}
          {stage === 'confirmClose' && renderConfirmClose()}
          {stage === 'confirmPermanent' && renderConfirmPermanent()}
          {stage === 'submitting' && renderSubmitting()}
        </div>

        {/* Footer */}
        {stage === 'choose' && (
          <div className="p-6 pt-2 border-t flex justify-end flex-shrink-0">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
